import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import { Box, Divider, Link, Rating, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';

function InfoRow({ icon: Icon, children, href = null }) {
  const content = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{ py: 0.35 }}>
      <Box
        sx={{
          mt: 0.15,
          display: 'flex',
          color: 'text.secondary',
          flexShrink: 0,
        }}>
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Stack>
  );

  if (href) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        color="inherit"
        sx={{ '&:hover': { color: 'primary.main' } }}>
        {content}
      </Link>
    );
  }

  return content;
}

/**
 * Service directory / technician (place) detail card for map overlays or side panels.
 *
 * @param {object} props
 * @param {object} props.technician — Business or place payload (name, rating, address, etc.)
 * @param {object} [props.sx] — MUI `sx` merged onto the root Paper
 */
function TechnicianDetails({ technician }) {
  const theme = useTheme();

  if (!technician || typeof technician !== 'object') {
    return 'No Information Found';
  }

  const {
    name,
    rating,
    review_count: reviewCount,
    address,
    phone,
    website,
    summary,
    reviewSummary,
  } = technician;

  return (
    <Box>
      <Stack spacing={2} sx={{ pl: 2, pr: 2.5, pb: 2.25 }}>
        <Stack spacing={1.25}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}>
            <Typography
              component="h2"
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: -0.02,
              }}>
              {name.toUpperCase() || '—'}
            </Typography>
          </Stack>

          {rating != null && Number.isFinite(Number(rating)) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap">
              <Rating
                name="technician-rating"
                value={Number(rating)}
                precision={0.1}
                readOnly
                size="small"
                sx={{ color: 'warning.main' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}>
                {Number(rating).toFixed(1)}
              </Typography>
              {reviewCount != null && (
                <Typography variant="body2" color="text.secondary">
                  ({Number(reviewCount).toLocaleString()} reviews)
                </Typography>
              )}
            </Stack>
          )}
        </Stack>

        <Divider
          flexItem
          sx={{ borderColor: alpha(theme.palette.divider, 0.9) }}
        />

        <Stack spacing={0.25}>
          {address && (
            <InfoRow icon={LocationOnOutlined}>
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                {address}
              </Typography>
            </InfoRow>
          )}

          {phone && (
            <InfoRow icon={PhoneOutlined}>
              <Typography variant="body2" fontWeight={500}>
                {phone}
              </Typography>
            </InfoRow>
          )}

          {website && (
            <InfoRow icon={LanguageOutlined} href={website}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  wordBreak: 'break-word',
                  color: 'primary.main',
                }}>
                {website.replace(/^https?:\/\//i, '')}
              </Typography>
            </InfoRow>
          )}
        </Stack>

        {(summary || reviewSummary) && (
          <>
            <Divider
              flexItem
              sx={{ borderColor: alpha(theme.palette.divider, 0.9) }}
            />
            <Stack spacing={1.5}>
              {summary ? (
                <Box
                  sx={{
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: t =>
                      alpha(
                        t.palette.primary.main,
                        t.palette.mode === 'dark' ? 0.12 : 0.06,
                      ),
                    border: 1,
                    borderColor: t =>
                      alpha(
                        t.palette.primary.main,
                        t.palette.mode === 'dark' ? 0.25 : 0.12,
                      ),
                  }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mb: 0.75 }}>
                    <BusinessCenterOutlined
                      sx={{ fontSize: 18, color: 'primary.main' }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="primary">
                      About
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      typography: 'body2',
                      color: 'text.secondary',
                      lineHeight: 1.6,
                    }}>
                    <ReadMoreText maxChars={160} allowExpand>
                      {summary}
                    </ReadMoreText>
                  </Box>
                </Box>
              ) : null}

              {reviewSummary ? (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 0.5 }}>
                    Review highlights
                  </Typography>
                  <Box
                    sx={{
                      typography: 'body2',
                      color: 'text.secondary',
                      lineHeight: 1.6,
                    }}>
                    <ReadMoreText maxChars={160} allowExpand>
                      {reviewSummary}
                    </ReadMoreText>
                  </Box>
                </Box>
              ) : null}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

export default TechnicianDetails;
